import React, { useRef, useState, useEffect } from "react";
import api, { mediaUrl } from "@/lib/api";
import { Upload, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export const Label = ({ children }) => (
  <label className="block text-[0.68rem] tracking-[0.14em] uppercase text-espresso/60 mb-1.5">{children}</label>
);

// Money parser: treats comma as a decimal separator (245,00 -> 245.00), strips
// stray thousands separators, rejects negatives. Prevents "245,00" saving as 24500.
export const parseMoney = (raw) => {
  if (raw == null || String(raw).trim() === "") return null;
  let t = String(raw).replace(/[^0-9.,]/g, "").replace(/,/g, ".");
  const parts = t.split(".");
  if (parts.length > 2) t = parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
  const n = parseFloat(t);
  if (isNaN(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
};

export const MoneyField = ({ label, value, onChange, placeholder, testid }) => {
  const [txt, setTxt] = useState(value == null ? "" : String(value));
  useEffect(() => { setTxt(value == null ? "" : String(value)); }, [value]);
  const handle = (e) => { const raw = e.target.value; setTxt(raw); onChange(parseMoney(raw)); };
  const commit = () => { const n = parseMoney(txt); setTxt(n == null ? "" : String(n)); };
  return (
    <div className="mb-4">
      <Label>{label}</Label>
      <input inputMode="decimal" value={txt} placeholder={placeholder} data-testid={testid}
        onChange={handle} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
        className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
    </div>
  );
};

export const Field = ({ label, value, onChange, textarea, type = "text", placeholder, testid }) => (
  <div className="mb-4">
    <Label>{label}</Label>
    {textarea ? (
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4} data-testid={testid}
        className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso resize-y" />
    ) : (
      <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
    )}
  </div>
);

export const Select = ({ label, value, onChange, options, testid }) => (
  <div className="mb-4">
    <Label>{label}</Label>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso">
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  </div>
);

export const Toggle = ({ label, value, onChange, testid }) => (
  <label className="flex items-center justify-between gap-4 mb-4 cursor-pointer">
    <span className="text-[0.68rem] tracking-[0.14em] uppercase text-espresso/60">{label}</span>
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}
      role="switch" aria-checked={!!value} aria-label={label || testid || "toggle"}
      className={`w-11 h-6 rounded-full transition-colors relative ${value ? "bg-sage" : "bg-espresso/20"}`}>
      <span className={`absolute top-0.5 w-5 h-5 bg-cream rounded-full transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </label>
);

export const ImageUpload = ({ label, value, onChange, onDraft, testid }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
      if (onDraft) onDraft(data.url);
    } catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="mb-4">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="w-20 h-24 bg-ivory border border-espresso/20 overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <span className="text-espresso/30 text-xs">None</span>}
        </div>
        <div className="flex-1">
          <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" data-testid={testid}
            className="w-full bg-ivory border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso mb-2" />
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => ref.current?.click()} disabled={busy}
              className="flex items-center gap-2 text-[0.68rem] tracking-[0.14em] uppercase border border-espresso px-3 py-2 hover:bg-espresso hover:text-cream transition-colors">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} data-testid={testid ? `${testid}-remove` : "imageupload-remove"}
                className="text-[0.68rem] tracking-[0.14em] uppercase border border-wine text-wine px-3 py-2 hover:bg-wine hover:text-cream transition-colors">
                Remove
              </button>
            )}
            {onDraft && value && (
              <button type="button" onClick={() => onDraft(value)} data-testid="imageupload-draft"
                className="text-[0.68rem] tracking-[0.14em] uppercase border border-gold text-espresso px-3 py-2 hover:bg-gold hover:text-cocoa transition-colors">
                Draft from photo
              </button>
            )}
          </div>
          <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
        </div>
      </div>
    </div>
  );
};

export const FilmUpload = ({ label, value, onChange, testid }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Video uploaded");
    } catch { toast.error("Upload failed — very large videos may need to be under ~50MB"); }
    setBusy(false);
  };
  return (
    <div className="mb-4">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="w-20 h-24 bg-cocoa border border-espresso/20 overflow-hidden flex items-center justify-center shrink-0">
          {value ? <video src={mediaUrl(value)} muted loop autoPlay playsInline className="w-full h-full object-cover" /> : <span className="text-cream/40 text-xs">None</span>}
        </div>
        <div className="flex-1">
          <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Video URL or upload" data-testid={testid}
            className="w-full bg-ivory border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso mb-2" />
          <div className="flex gap-2 items-center">
            <button type="button" onClick={() => ref.current?.click()} disabled={busy}
              className="flex items-center gap-2 text-[0.68rem] tracking-[0.14em] uppercase border border-espresso px-3 py-2 hover:bg-espresso hover:text-cream transition-colors">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload video
            </button>
            {value && <button type="button" onClick={() => onChange("")} className="text-[0.68rem] tracking-[0.14em] uppercase text-wine px-2 py-2">Remove</button>}
          </div>
          <p className="text-[0.62rem] text-espresso/40 mt-1.5">Plays muted &amp; looping at the top of the page. Vertical, ~5–15s works best. It leads; your photos follow.</p>
          <input ref={ref} type="file" accept="video/*" hidden onChange={(e) => upload(e.target.files[0])} />
        </div>
      </div>
    </div>
  );
};

export const Card = ({ children, className = "" }) => (
  <div className={`bg-cream border border-espresso/10 p-6 ${className}`}>{children}</div>
);

export const Btn = ({ children, onClick, variant = "primary", type = "button", testid, disabled }) => {
  const cls = variant === "primary"
    ? "bg-espresso text-cream hover:bg-cocoa"
    : variant === "danger" ? "bg-wine text-cream hover:opacity-90"
    : "border border-espresso hover:bg-espresso hover:text-cream";
  return <button type={type} onClick={onClick} data-testid={testid} disabled={disabled}
    className={`px-5 py-2.5 text-[0.68rem] tracking-[0.16em] uppercase transition-colors disabled:opacity-50 ${cls}`}>{children}</button>;
};

export const AiSeoButton = ({ subject, context, department, onResult, testid }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai/seo", { subject: subject || "", context: context || "", department: department || "" });
      onResult(data);
      toast.success("SEO draft ready — review & edit");
    } catch { toast.error("Could not draft"); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid={testid}
      className="flex items-center gap-2 text-[0.66rem] tracking-[0.14em] uppercase border border-espresso/40 px-3 py-1.5 hover:bg-espresso hover:text-cream transition-colors mb-4 disabled:opacity-50">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Draft SEO with AI
    </button>
  );
};

export const SeoPreview = ({ title, description, path }) => {
  if (!title && !description) return null;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  return (
    <div className="mb-4 border border-espresso/10 bg-ivory p-3" data-testid="seo-preview">
      <p className="text-[0.58rem] tracking-[0.16em] uppercase text-espresso/40 mb-1.5">Search preview</p>
      <p className="text-[#1a0dab] text-base leading-snug truncate">{title || "Page title"}</p>
      <p className="text-[#006621] text-xs">{base}{path || ""}</p>
      <p className="text-espresso/70 text-xs leading-snug mt-0.5">{description || "Your meta description will appear here."}</p>
    </div>
  );
};

export const PageHead = ({ title, sub, children }) => (
  <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
    <div>
      <h1 className="font-serif text-4xl leading-none">{title}</h1>
      {sub && <p className="text-espresso/50 text-sm mt-2">{sub}</p>}
    </div>
    <div className="flex gap-3">{children}</div>
  </div>
);

export const AiCopyButton = ({ instruction: defaultInstr, current, onResult, testid }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const instruction = window.prompt("What should the assistant write? (e.g. 'Draft an About page telling our heritage story')", defaultInstr || "");
    if (!instruction) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai/copy", { instruction, current: current || "" });
      onResult(data.text);
      toast.success("Draft ready — review & edit");
    } catch { toast.error("Could not draft"); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid={testid}
      className="flex items-center gap-2 text-[0.66rem] tracking-[0.14em] uppercase border border-espresso/40 px-3 py-1.5 hover:bg-espresso hover:text-cream transition-colors mb-4 disabled:opacity-50">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Draft with AI
    </button>
  );
};
